import { Injectable } from "@angular/core";
import {
  FirebaseApp,
  deleteApp,
  getApps,
  initializeApp,
} from "firebase/app";
import { Firestore, getFirestore } from "firebase/firestore";

export interface FirebaseConfig {
  apiKey: string;
  authDomain: string;
  databaseURL?: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
}

@Injectable({ providedIn: "root" })
export class FirebaseService {
  private app: FirebaseApp | null = null;
  private db: Firestore | null = null;
  private currentProjectId: string | null = null;

  /**
   * Inicializa (ou re-inicializa) o Firebase com o config do tenant atual.
   * Se já existir uma app com outro projectId, destroi e recria.
   */
  initialize(config?: FirebaseConfig): Firestore | null {
    if (!config || !config.apiKey || !config.projectId) {
      console.warn(
        "FirebaseService: config inválido, Firebase não inicializado."
      );
      return null;
    }

    // Se já está inicializado com o mesmo projeto, reutiliza
    if (this.currentProjectId === config.projectId && this.db) {
      return this.db;
    }

    // Destroi app anterior se existir
    this.destroy();

    try {
      // Se existem apps criadas externamente, limpa-as primeiro
      const existingApps = getApps();
      for (const app of existingApps) {
        try { deleteApp(app); } catch {}
      }

      this.app = initializeApp(config);
      this.db = getFirestore(this.app);
      this.currentProjectId = config.projectId;
      return this.db;
    } catch (error) {
      console.error("FirebaseService: Erro ao inicializar Firebase", error);
      return null;
    }
  }

  /**
   * Retorna a instância do Firestore (ou null se não inicializado).
   */
  getFirestoreDb(): Firestore | null {
    return this.db;
  }

  /**
   * Retorna o nome da collection do Firebase para o tenant logado.
   */
  getCollection(): string {
    return localStorage.getItem("firebaseCollection") || "location_qa";
  }

  /**
   * Carrega o config do localStorage e inicializa o Firebase.
   * Retorna o Firestore ou null se não houver config salvo.
   */
  initializeFromStorage(): Firestore | null {
    const configStr = localStorage.getItem("firebaseConfig");
    if (!configStr) {
      return null;
    }

    try {
      const config: FirebaseConfig = JSON.parse(configStr);
      return this.initialize(config);
    } catch {
      console.warn("FirebaseService: Falha ao parsear firebaseConfig do localStorage.");
      return null;
    }
  }

  /**
   * Destroi a instância atual do Firebase.
   */
  destroy(): void {
    if (this.app) {
      try {
        deleteApp(this.app);
      } catch {}
      this.app = null;
      this.db = null;
      this.currentProjectId = null;
    }
  }

  /**
   * Limpa os dados do Firebase do localStorage e destroi a instância.
   */
  clearAndDestroy(): void {
    localStorage.removeItem("firebaseConfig");
    localStorage.removeItem("firebaseCollection");
    this.destroy();
  }
}
